// Host-controller preview-grant helper.
//
// Sign-in happens entirely in the host controller now (web Google sign-in — see
// webAuth.ts); the extension no longer authenticates, so there's no extension
// session to reuse. This module just builds the URL for the drive.file Picker
// grant page, which authenticates with the host's Copresent session and mints
// its token from the backend.

// Google Cloud project number for the drive.file Picker (public — same value
// the extension shipped). Exported because the grant page uses OUR value rather
// than the one in the URL: the extension puts an appId in the query string, and
// trusting a caller-supplied project number is a needless degree of freedom when
// both sides are hard-coded to the same constant anyway.
export const GOOGLE_APP_ID = process.env.REACT_APP_GOOGLE_APP_ID ?? "144955778233";

// The one extension allowed to drive the grant page. The page messages this id
// back on a successful grant, so it must never be taken from the URL.
export const EXTENSION_ID = process.env.REACT_APP_EXTENSION_ID ?? "";

// Where /enable-preview should send the host once the grant is done.
const PREVIEW_RETURN_PARAM = "return";

// The read half of that round trip, kept next to the write half so the two
// can't drift.
//
// SAME-ORIGIN ONLY. The value comes off the query string and is handed to
// location.replace, so an unchecked one is an open redirect sitting directly
// downstream of a Google OAuth token. Parsing against our own origin and then
// re-checking `origin` is what rejects `//evil.example` (protocol-relative,
// which resolves to a foreign origin) and `javascript:` (which parses fine as
// a URL and would otherwise reach an href). Only path/search/hash survives, so
// nothing else can be smuggled through.
export const readPreviewReturnTo = (search: string): string => {
  const raw = new URLSearchParams(search).get(PREVIEW_RETURN_PARAM);
  if (!raw) return "";
  try {
    const url = new URL(raw, window.location.origin);
    if (url.origin !== window.location.origin) return "";
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return "";
  }
};

export const previewGrantUrl = (
  deckId: string,
  magicId: string,
  returnTo?: string
): string => {
  const params = new URLSearchParams({
    deck: deckId,
    magicId,
    appId: GOOGLE_APP_ID,
  });
  if (returnTo) params.set(PREVIEW_RETURN_PARAM, returnTo);
  return `${window.location.origin}/enable-preview?${params.toString()}`;
};

// Open the Picker grant page to enable live previews for a deck. The page gets a
// drive.file token from the backend (host session) and, once the host picks the
// deck, tells the backend to render.
//
// `sameTab` navigates instead of popping. That's the only version of this that
// finishes on a phone: iOS Safari ships with the pop-up blocker ON, the
// width/height hints are ignored, and a tab that can't `window.close()` itself
// strands the host on the grant page with no way back to their presentation. The
// same-tab flow carries a `return` param so the page can send them back.
//
// Returns whether the grant page was actually reached — `false` for missing ids,
// and `false` for a popup the browser refused.
export const openPreviewGrant = (
  deckId: string,
  magicId: string,
  opts: { sameTab?: boolean } = {}
): boolean => {
  if (!deckId || !magicId) return false;

  if (opts.sameTab) {
    window.location.assign(
      previewGrantUrl(deckId, magicId, window.location.href)
    );
    return true;
  }

  // Deliberately NO `noopener`: the popup is our own same-origin page, and the
  // handle is the only way to tell "blocked" from "opened" — with noopener,
  // window.open always returns null per spec, so a blocked popup was
  // indistinguishable from a working one and this reported success either way.
  const win = window.open(
    previewGrantUrl(deckId, magicId),
    "_blank",
    "width=480,height=640"
  );
  return !!win;
};
